import React, { useState, useEffect, useCallback } from 'react';
import { financeApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Sparkle, Trash, Check, X } from '@phosphor-icons/react';
import { ObjectPageHeader, SectionTabs, FioriTable, StatusPill, STATUS_COLOR, fmtCurrency } from '../components/fiori';

const TABS = [
  { key: 'invoices',  label: 'Invoices' },
  { key: 'expenses',  label: 'Expenses' },
  { key: 'sales',     label: 'Sales Orders' },
  { key: 'budgets',   label: 'Budgets' },
  { key: 'journals',  label: 'Journals' },
  { key: 'accounts',  label: 'Chart of Accounts' },
];

export default function FinancePage() {
  const [active, setActive] = useState('invoices');
  const [summary, setSummary] = useState({});
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // 'invoice' | 'expense' | 'sales' | 'budget' | 'account' | 'journal' | null

  const loadSummary = useCallback(async () => {
    try { const r = await financeApi.summary(); setSummary(r.data); } catch {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let r;
      if (active === 'invoices') r = await financeApi.invoices.list();
      else if (active === 'expenses') r = await financeApi.expenses.list();
      else if (active === 'sales') r = await financeApi.salesOrders.list();
      else if (active === 'budgets') r = await financeApi.budgets.list();
      else if (active === 'journals') r = await financeApi.journals.list();
      else if (active === 'accounts') r = await financeApi.accounts.list();
      setData(r?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [active]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { load(); }, [load]);

  const invTotal = Object.values(summary.invoices || {}).reduce((a, b) => a + (b.total || 0), 0);
  const expTotal = Object.values(summary.expenses || {}).reduce((a, b) => a + (b.total || 0), 0);
  const salesTotal = Object.values(summary.sales || {}).reduce((a, b) => a + (b.total || 0), 0);
  const unpaidInv = (summary.invoices?.sent?.total || 0) + (summary.invoices?.overdue?.total || 0);

  const kpis = [
    { label: 'Invoices billed', value: fmtCurrency(invTotal) },
    { label: 'Outstanding',      value: fmtCurrency(unpaidInv), color: '#E4002B' },
    { label: 'Expenses YTD',     value: fmtCurrency(expTotal), color: '#FFC72C' },
    { label: 'Sales pipeline',   value: fmtCurrency(salesTotal), color: '#00B140' },
  ];

  return (
    <div data-testid="finance-page">
      <ObjectPageHeader
        breadcrumb={['Home', 'Finance']}
        title="Finance"
        subtitle="Invoices, expenses, sales orders, budgets and journal entries"
        kpis={kpis}
        actions={
          <Button
            className="rounded-sm h-9"
            style={{ background: '#002677' }}
            onClick={() => setDialog(active === 'sales' ? 'sales'
              : active === 'budgets' ? 'budget'
              : active === 'accounts' ? 'account'
              : active === 'journals' ? 'journal'
              : active === 'expenses' ? 'expense'
              : 'invoice')}
            data-testid="finance-create-btn"
          >
            <Plus size={16} className="mr-1.5" /> New
          </Button>
        }
      />

      <SectionTabs tabs={TABS} active={active} onChange={setActive} />

      <div className="p-6">
        {loading ? (
          <div className="text-center py-10 text-sm text-muted-foreground">Loading…</div>
        ) : (
          <>
            {active === 'invoices'  && <InvoicesTable rows={data} reload={load} />}
            {active === 'expenses'  && <ExpensesTable rows={data} reload={load} />}
            {active === 'sales'     && <SalesTable rows={data} reload={load} />}
            {active === 'budgets'   && <BudgetsTable rows={data} reload={load} />}
            {active === 'journals'  && <JournalsTable rows={data} />}
            {active === 'accounts'  && <AccountsTable rows={data} reload={load} />}
          </>
        )}
      </div>

      {dialog === 'invoice'  && <InvoiceForm onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); loadSummary(); }} />}
      {dialog === 'expense'  && <ExpenseForm onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); loadSummary(); }} />}
      {dialog === 'sales'    && <SalesForm onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); loadSummary(); }} />}
      {dialog === 'budget'   && <BudgetForm onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
      {dialog === 'account'  && <AccountForm onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
      {dialog === 'journal'  && <JournalForm onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}

/* ------------- Table cells ------------- */

function InvoicesTable({ rows, reload }) {
  const setStatus = async (id, status) => {
    try { await financeApi.invoices.setStatus(id, status); toast.success(`Invoice ${status}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    try { await financeApi.invoices.delete(id); toast.success('Invoice deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <FioriTable
      columns={[
        { key: 'number',     label: 'Invoice #' },
        { key: 'customer_name', label: 'Customer' },
        { key: 'issue_date', label: 'Issued', render: r => r.issue_date?.slice(0, 10) },
        { key: 'due_date',   label: 'Due',    render: r => r.due_date?.slice(0, 10) },
        { key: 'total',      label: 'Total',  render: r => <span className="font-semibold">{fmtCurrency(r.total)}</span> },
        { key: 'status',     label: 'Status', render: r => <StatusPill status={r.status} /> },
        { key: 'actions',    label: '', width: '160px', render: r => (
            <div className="flex items-center gap-1 justify-end">
              {r.status !== 'paid' && <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => setStatus(r.id, 'paid')}>Mark paid</Button>}
              {r.status === 'draft' && <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => setStatus(r.id, 'sent')}>Send</Button>}
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => del(r.id)}><Trash size={14} /></Button>
            </div>
          ) },
      ]}
      rows={rows}
      rowStatusColor={(r) => STATUS_COLOR[r.status] || '#8A8E99'}
      emptyLabel="No invoices yet — click New to create one"
    />
  );
}

function ExpensesTable({ rows, reload }) {
  const setStatus = async (id, status) => {
    try { await financeApi.expenses.setStatus(id, status); toast.success(`Expense ${status}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <FioriTable
      columns={[
        { key: 'date',     label: 'Date', render: r => r.date?.slice(0, 10) },
        { key: 'vendor',   label: 'Vendor' },
        { key: 'category', label: 'Category' },
        { key: 'description', label: 'Description' },
        { key: 'amount',   label: 'Amount', render: r => <span className="font-semibold">{fmtCurrency(r.amount, r.currency)}</span> },
        { key: 'submitter_name', label: 'Submitted by' },
        { key: 'status',   label: 'Status', render: r => <StatusPill status={r.status} /> },
        { key: 'actions',  label: '', width: '200px', render: r => (
            r.status === 'pending' ? (
              <div className="flex gap-1 justify-end">
                <Button size="sm" className="h-7 text-xs rounded-sm" style={{ background: '#00B140' }} onClick={() => setStatus(r.id, 'approved')}><Check size={12} className="mr-1" />Approve</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => setStatus(r.id, 'rejected')}><X size={12} className="mr-1" />Reject</Button>
              </div>
            ) : r.status === 'approved' ? (
              <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => setStatus(r.id, 'reimbursed')}>Mark reimbursed</Button>
            ) : null
          ) },
      ]}
      rows={rows}
      rowStatusColor={(r) => STATUS_COLOR[r.status] || '#8A8E99'}
      emptyLabel="No expenses submitted"
    />
  );
}

function SalesTable({ rows, reload }) {
  const setStatus = async (id, status) => {
    try { await financeApi.salesOrders.setStatus(id, status); toast.success(`Order ${status}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <FioriTable
      columns={[
        { key: 'number',        label: 'Order #' },
        { key: 'customer_name', label: 'Customer' },
        { key: 'order_date',    label: 'Ordered', render: r => r.order_date?.slice(0, 10) },
        { key: 'total',         label: 'Total',   render: r => <span className="font-semibold">{fmtCurrency(r.total)}</span> },
        { key: 'status',        label: 'Status',  render: r => <StatusPill status={r.status} /> },
        { key: 'actions', label: '', width: '220px', render: r => {
            const next = { draft: 'confirmed', confirmed: 'shipped', shipped: 'delivered' }[r.status];
            return next ? <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => setStatus(r.id, next)}>Advance to {next}</Button> : null;
          } },
      ]}
      rows={rows}
      rowStatusColor={(r) => STATUS_COLOR[r.status] || '#8A8E99'}
      emptyLabel="No sales orders"
    />
  );
}

function BudgetsTable({ rows, reload }) {
  const del = async (id) => {
    try { await financeApi.budgets.delete(id); toast.success('Budget deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <FioriTable
      columns={[
        { key: 'name',     label: 'Budget' },
        { key: 'period',   label: 'Period' },
        { key: 'owner',    label: 'Owner' },
        { key: 'category', label: 'Category' },
        { key: 'amount',   label: 'Planned', render: r => <span className="font-semibold">{fmtCurrency(r.amount)}</span> },
        { key: 'actual',   label: 'Actual',  render: r => fmtCurrency(r.actual) },
        { key: 'variance', label: 'Variance', render: r => (
            <span style={{ color: r.variance >= 0 ? '#00B140' : '#E4002B' }} className="font-medium">
              {fmtCurrency(r.variance)} ({r.variance_pct}%)
            </span>
          ) },
        { key: 'actions', label: '', width: '50px', render: r => <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => del(r.id)}><Trash size={14} /></Button> },
      ]}
      rows={rows}
      rowStatusColor={(r) => (r.variance >= 0 ? '#00B140' : '#E4002B')}
      emptyLabel="No budgets yet"
    />
  );
}

function JournalsTable({ rows }) {
  return (
    <FioriTable
      columns={[
        { key: 'date', label: 'Date', render: r => r.date?.slice(0, 10) },
        { key: 'reference', label: 'Reference' },
        { key: 'description', label: 'Description' },
        { key: 'lines', label: 'Lines', render: r => `${r.lines?.length || 0} lines` },
        { key: 'total_debit', label: 'Total', render: r => fmtCurrency(r.total_debit) },
        { key: 'posted', label: 'Status', render: r => <StatusPill status={r.posted ? 'approved' : 'draft'} /> },
      ]}
      rows={rows}
      rowStatusColor={() => '#002677'}
      emptyLabel="No journal entries"
    />
  );
}

function AccountsTable({ rows, reload }) {
  const del = async (id) => {
    try { await financeApi.accounts.delete(id); toast.success('Account deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const typeColor = { asset: '#00B140', liability: '#E4002B', equity: '#002677', revenue: '#00B140', expense: '#FFC72C' };
  return (
    <FioriTable
      columns={[
        { key: 'code', label: 'Code', width: '100px' },
        { key: 'name', label: 'Name' },
        { key: 'type', label: 'Type', render: r => <span className="text-xs uppercase font-semibold tracking-wider" style={{ color: typeColor[r.type] || '#000' }}>{r.type}</span> },
        { key: 'balance', label: 'Balance', render: r => fmtCurrency(r.balance) },
        { key: 'actions', label: '', width: '50px', render: r => <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => del(r.id)}><Trash size={14} /></Button> },
      ]}
      rows={rows}
      rowStatusColor={(r) => typeColor[r.type] || '#8A8E99'}
      emptyLabel="No accounts defined yet"
    />
  );
}

/* ------------- Create forms ------------- */

function BaseDialog({ title, onClose, children, onSubmit, submitting, submitLabel = 'Create' }) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg rounded-sm">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 py-2">
          {children}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button type="submit" className="rounded-sm" style={{ background: '#002677' }} disabled={submitting}>
              {submitting ? 'Saving…' : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceForm({ onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const plus30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({ customer_name: '', issue_date: today, due_date: plus30, description: '', amount: '', tax_rate: 20 });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await financeApi.invoices.create({
        customer_name: form.customer_name, issue_date: form.issue_date, due_date: form.due_date,
        line_items: [{ description: form.description, quantity: 1, unit_price: parseFloat(form.amount), tax_rate: parseFloat(form.tax_rate) }],
      });
      toast.success('Invoice created');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <BaseDialog title="New invoice" onClose={onClose} onSubmit={submit} submitting={saving}>
      <Field label="Customer name" ><Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} required /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Issue date"><Input type="date" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} required /></Field>
        <Field label="Due date"><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} required /></Field>
      </div>
      <Field label="Description"><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Amount (pre-tax)"><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required /></Field>
        <Field label="VAT %"><Input type="number" step="0.1" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: e.target.value })} /></Field>
      </div>
    </BaseDialog>
  );
}

function ExpenseForm({ onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date: today, vendor: '', category: '', description: '', amount: '' });
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const suggest = async () => {
    setAiLoading(true);
    try {
      const { data } = await financeApi.expenses.aiCategorize({ vendor: form.vendor, description: form.description, amount: form.amount });
      setForm(f => ({ ...f, category: data.category }));
      toast.success(`AI suggests: ${data.category}`);
    } catch { toast.error('AI categorize failed'); }
    finally { setAiLoading(false); }
  };
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await financeApi.expenses.create({ ...form, amount: parseFloat(form.amount) });
      toast.success('Expense submitted'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <BaseDialog title="Submit expense" onClose={onClose} onSubmit={submit} submitting={saving}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date"><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required /></Field>
        <Field label="Amount"><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required /></Field>
      </div>
      <Field label="Vendor"><Input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} required /></Field>
      <Field label="Description"><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required /></Field>
      <Field label="Category">
        <div className="flex gap-2">
          <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} required placeholder="e.g. Travel" />
          <Button type="button" variant="outline" className="rounded-sm whitespace-nowrap" onClick={suggest} disabled={aiLoading || !form.vendor}>
            <Sparkle size={14} className="mr-1" />{aiLoading ? 'Thinking…' : 'AI suggest'}
          </Button>
        </div>
      </Field>
    </BaseDialog>
  );
}

function SalesForm({ onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ customer_name: '', order_date: today, description: '', qty: 1, unit_price: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await financeApi.salesOrders.create({
        customer_name: form.customer_name, order_date: form.order_date,
        line_items: [{ description: form.description, quantity: parseFloat(form.qty), unit_price: parseFloat(form.unit_price) }],
      });
      toast.success('Sales order created'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <BaseDialog title="New sales order" onClose={onClose} onSubmit={submit} submitting={saving}>
      <Field label="Customer"><Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} required /></Field>
      <Field label="Order date"><Input type="date" value={form.order_date} onChange={e => setForm({ ...form, order_date: e.target.value })} required /></Field>
      <Field label="Description"><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Quantity"><Input type="number" value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} required /></Field>
        <Field label="Unit price"><Input type="number" step="0.01" value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} required /></Field>
      </div>
    </BaseDialog>
  );
}

function BudgetForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', period: new Date().getFullYear().toString(), owner: '', amount: '', category: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await financeApi.budgets.create({ ...form, amount: parseFloat(form.amount) });
      toast.success('Budget created'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <BaseDialog title="New budget" onClose={onClose} onSubmit={submit} submitting={saving}>
      <Field label="Budget name"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Period (YYYY or YYYY-Qn)"><Input value={form.period} onChange={e => setForm({ ...form, period: e.target.value })} required /></Field>
        <Field label="Owner"><Input value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })} required placeholder="Dept or project" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Amount"><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required /></Field>
        <Field label="Category (links to expenses)"><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="e.g. Marketing" /></Field>
      </div>
    </BaseDialog>
  );
}

function AccountForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ code: '', name: '', type: 'asset' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await financeApi.accounts.create(form);
      toast.success('Account created'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <BaseDialog title="New account" onClose={onClose} onSubmit={submit} submitting={saving}>
      <Field label="Account code"><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} required placeholder="1100" /></Field>
      <Field label="Account name"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Bank account" /></Field>
      <Field label="Type">
        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
          <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="asset">Asset</SelectItem>
            <SelectItem value="liability">Liability</SelectItem>
            <SelectItem value="equity">Equity</SelectItem>
            <SelectItem value="revenue">Revenue</SelectItem>
            <SelectItem value="expense">Expense</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </BaseDialog>
  );
}

function JournalForm({ onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date: today, description: '', reference: '', debit_account: '', credit_account: '', amount: '' });
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { financeApi.accounts.list().then(r => setAccounts(r.data)).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const amt = parseFloat(form.amount);
      await financeApi.journals.create({
        date: form.date, description: form.description, reference: form.reference,
        lines: [
          { account_id: form.debit_account,  debit: amt, credit: 0 },
          { account_id: form.credit_account, debit: 0, credit: amt },
        ],
      });
      toast.success('Journal entry posted'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <BaseDialog title="New journal entry" onClose={onClose} onSubmit={submit} submitting={saving}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date"><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required /></Field>
        <Field label="Reference"><Input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} /></Field>
      </div>
      <Field label="Description"><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required /></Field>
      <Field label="Debit account">
        <AccountPicker accounts={accounts} value={form.debit_account} onChange={(v) => setForm({ ...form, debit_account: v })} />
      </Field>
      <Field label="Credit account">
        <AccountPicker accounts={accounts} value={form.credit_account} onChange={(v) => setForm({ ...form, credit_account: v })} />
      </Field>
      <Field label="Amount"><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required /></Field>
    </BaseDialog>
  );
}

function AccountPicker({ accounts, value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-sm"><SelectValue placeholder="Pick account" /></SelectTrigger>
      <SelectContent>
        {accounts.length === 0 && <div className="p-2 text-xs text-muted-foreground">Add accounts first in Chart of Accounts</div>}
        {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
